import { useState } from 'react';
import { useQuery } from 'react-query';
import { inventarioService, MovimientoInventario } from '../../services/inventarioService';
import { sucursalService } from '../../services/sucursalService';

const MovimientosList = () => {
  const [page, setPage] = useState(1);
  const [sucursalFilter, setSucursalFilter] = useState<number | undefined>(undefined);
  const [tipoFilter, setTipoFilter] = useState<string>('');

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const { data, isLoading } = useQuery(
    ['movimientos', page, sucursalFilter, tipoFilter],
    () => inventarioService.getMovimientos(page, 50, undefined, sucursalFilter, tipoFilter || undefined)
  );

  if (isLoading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  const tipos = [
    { value: '', label: 'Todos' },
    { value: 'entrada', label: 'Entrada' },
    { value: 'salida', label: 'Salida' },
    { value: 'ajuste', label: 'Ajuste' },
    { value: 'merma', label: 'Merma' },
    { value: 'venta', label: 'Venta' },
    { value: 'devolucion', label: 'Devolución' }
  ];

  const getTipoColor = (tipo: string) => {
    const colors: { [key: string]: string } = {
      entrada: 'bg-green-100 text-green-800',
      salida: 'bg-red-100 text-red-800',
      ajuste: 'bg-blue-100 text-blue-800',
      merma: 'bg-orange-100 text-orange-800',
      venta: 'bg-purple-100 text-purple-800',
      devolucion: 'bg-yellow-100 text-yellow-800'
    };
    return colors[tipo] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Movimientos de Inventario</h1>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b space-y-4">
          <div className="flex space-x-4">
            <select
              value={sucursalFilter || ''}
              onChange={(e) => {
                setSucursalFilter(e.target.value ? Number(e.target.value) : undefined);
                setPage(1);
              }}
              className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Todas las sucursales</option>
              {sucursalesData?.data.sucursales.map((sucursal) => (
                <option key={sucursal.id} value={sucursal.id}>
                  {sucursal.name}
                </option>
              ))}
            </select>
            <select
              value={tipoFilter}
              onChange={(e) => {
                setTipoFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {tipos.map((tipo) => (
                <option key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sucursal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Costo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Motivo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data?.data.movimientos.map((movimiento) => (
                <tr key={movimiento.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {movimiento.createdAt ? new Date(movimiento.createdAt).toLocaleString() : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {movimiento.producto?.name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {movimiento.sucursal?.name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getTipoColor(movimiento.tipo)}`}>
                      {movimiento.tipo}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {Number(movimiento.cantidad) > 0 ? '+' : ''}{(Number(movimiento.cantidad) || 0).toFixed(3)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${(Number(movimiento.costoUnitario) || 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {movimiento.usuario?.name || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                    {movimiento.motivo || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data?.data.pagination && (
          <div className="p-4 border-t flex justify-between items-center">
            <div className="text-sm text-gray-700">
              Mostrando {((page - 1) * 50) + 1} a {Math.min(page * 50, data.data.pagination.total)} de {data.data.pagination.total}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= data.data.pagination.totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MovimientosList;

